/**
 * runEngine — чистая физика/генератор run-вопроса «Беги к углу» (Фаза 14,
 * эпик B1/B2; спека §3.1–§3.2 phase-14-interface-spec.md).
 *
 * Без React и DOM: детерминированная симуляция, одинаково разворачиваемая
 * игроком (RunView) и ведущим (RunHostView) по одному seed из kindData
 * (трафик дронов — 0 байт, план §1). Все функции чистые — юнит-тесты
 * напрямую (runEngine.test.ts); кейсы runZone зеркальны готестам сервера
 * [RunZone](../../server/internal/game/run.go) с теми же координатами
 * (прецедент FNV-шафла match).
 *
 * Единицы — нормализованные (доли плитки 0..1; скорость — плиток/сек; время — мс).
 */
import type { RunParams } from '../../protocol';

/**
 * Фикс-константы физики run — дубль RunPhysics из
 * [run.go](../../server/internal/game/run.go) (спека §3.1, решение О §7.2:
 * единый источник параметров — сервер, рассылка в kindData гарантирует
 * одинаковый спавнер при минорных версиях клиента). Синхронизировать вручную
 * при изменении серверных значений; fallback, если kindData.params отсутствует
 * (превью редактора). Калибровка — игротест фазы 14 (ОР2).
 *
 * 0.14.2 (игротест, решение владельца): скорости ×0.5 — игрок 0.55→0.275,
 * дроны 0.45→0.225 / 0.8→0.4 плиток/с; тайминги спавна не менялись
 * (t0Ms не зависят от скоростей, джиттер ±25% — множитель от новых скоростей).
 */
export const RUN_PHYSICS: RunParams = {
  moveSpeed: 0.275,
  playerRadius: 0.035,
  droneRadius: 0.03,
  firstDroneMs: 3500,
  spawnIntervalStartMs: 3500,
  spawnIntervalEndMs: 1500,
  droneSpeedStart: 0.225,
  droneSpeedEnd: 0.4,
  stunMs: 1800,
};

/** Ближняя граница зоны угла (строго меньше — внутри); зеркало runZoneNear. */
const ZONE_NEAR = 0.25;
/** Дальняя граница зоны угла (строго больше — внутри); зеркало runZoneFar. */
const ZONE_FAR = 0.75;

/**
 * Зона угла по нормализованным координатам (§3.2; редизайн 0.14.1, решение
 * владельца 2026-10-20): угол — квадрант 0.25×0.25 у соответствующей вершины
 * плитки (0 — левый-верхний, 1 — правый-верхний, 2 — левый-нижний,
 * 3 — правый-нижний); -1 — нейтральная зона (центральное ядро [0.25..0.75]²
 * и краевые полосы, включая точные границы 0.25/0.75 — консервативно «в
 * пользу не ответил», решение владельца 2026-10-19; уменьшенные зоны
 * усложняют добегание). Зеркало [game.RunZone](../../server/internal/game/
 * run.go): зачёт при close считает сервер по этой же геометрии, клиент —
 * индикатор зоны и счётчики ведущего; кейсы фиксируются зеркальными тестами.
 */
export function runZone(x: number, y: number): number {
  const nearX = x < ZONE_NEAR;
  const farX = x > ZONE_FAR;
  const nearY = y < ZONE_NEAR;
  const farY = y > ZONE_FAR;
  if (nearX && nearY) return 0;
  if (farX && nearY) return 1;
  if (nearX && farY) return 2;
  if (farX && farY) return 3;
  return -1;
}

/** Вектор направления ввода (единственная абстракция управления, решение Е §7.2). */
export interface InputVector {
  /** Компонента X: -1 влево … +1 вправо (|v| может быть < 1 — аналоговый джойстик). */
  x: number;
  /** Компонента Y: -1 вверх … +1 вниз (экранные координаты). */
  y: number;
}

/** Клавиши схемы «стрелки/WASD» (единый регистр через e.key.toLowerCase). */
const KEY_LEFT = new Set(['arrowleft', 'a']);
const KEY_RIGHT = new Set(['arrowright', 'd']);
const KEY_UP = new Set(['arrowup', 'w']);
const KEY_DOWN = new Set(['arrowdown', 's']);

/**
 * Вектор из множества удерживаемых клавиш (стрелки/WASD за одной схемой,
 * решение Е). Чистая функция — слушатели keydown/keyup живут в RunView,
 * здесь только отображение «клавиши → вектор» (юнит-тестируемо).
 */
export function keyboardVector(held: ReadonlySet<string>): InputVector {
  let x = 0;
  let y = 0;
  for (const key of held) {
    if (KEY_LEFT.has(key)) x -= 1;
    else if (KEY_RIGHT.has(key)) x += 1;
    else if (KEY_UP.has(key)) y -= 1;
    else if (KEY_DOWN.has(key)) y += 1;
  }
  return { x: Math.max(-1, Math.min(1, x)), y: Math.max(-1, Math.min(1, y)) };
}

/**
 * Вектор виртуального джойстика по смещению стика от центра (px): направление =
 * угол смещения, амплитуда = min(|offset|/radius, 1) — внутри «мёртвой зоны»
 * (клик без движения) вектор нулевой. Тач-схема из постановки задачи (решение Е:
 * обе тач-схемы v1 за одной абстракцией; финальный выбор — игротест ОР1).
 */
export function joystickVector(dx: number, dy: number, radius: number): InputVector {
  if (radius <= 0) return { x: 0, y: 0 };
  const len = Math.hypot(dx, dy);
  if (len < 1) return { x: 0, y: 0 };
  const mag = Math.min(1, len / radius);
  return { x: (dx / len) * mag, y: (dy / len) * mag };
}

function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(1, v));
}

/**
 * Шаг физики человечка: постоянная скорость moveSpeed вдоль v (|v| ≤ 1 —
 * диагональ не ускоряет), границы плитки — clamp 0..1. Возвращает новую
 * позицию; чистая функция без мутаций (client-authoritative физика, план §1).
 */
export function movePoint(
  x: number,
  y: number,
  v: InputVector,
  dtMs: number,
  params: RunParams,
): { x: number; y: number } {
  let { x: vx, y: vy } = v;
  const len = Math.hypot(vx, vy);
  if (len > 1) {
    vx /= len;
    vy /= len;
  }
  const dt = Math.max(0, dtMs) / 1000;
  return {
    x: clamp01(x + vx * params.moveSpeed * dt),
    y: clamp01(y + vy * params.moveSpeed * dt),
  };
}

/** Круг-круг коллизия (дрон × человечек, план B2). */
export function circleHit(
  ax: number,
  ay: number,
  ar: number,
  bx: number,
  by: number,
  br: number,
): boolean {
  const dx = ax - bx;
  const dy = ay - by;
  const r = ar + br;
  return dx * dx + dy * dy <= r * r;
}

/**
 * Проверка гибели игрока от дронов в момент elapsedMs (редизайн 0.14.1,
 * решение владельца 2026-10-20): круг-круг пересечение круга игрока
 * (x, y; params.playerRadius) с каждым АКТИВНЫМ дроном (params.droneRadius)
 * — порт [game.RunDroneHit](../../server/internal/game/run.go). Сервер —
 * источник истины зачёта; клиент детектит той же симуляцией для мгновенного
 * взрыва (r = 0.035+0.03 = 0.065 плитки, r² = 0.065²).
 */
export function droneHitAt(
  drones: readonly DroneSpec[],
  params: RunParams,
  x: number,
  y: number,
  elapsedMs: number,
): boolean {
  const r = params.playerRadius + params.droneRadius;
  const r2 = r * r;
  for (let i = 0; i < drones.length; i++) {
    const at = dronePosAt(drones[i], elapsedMs);
    if (at === null) continue; // ещё не заспавнен / уже улетел.
    const dx = at.x - x;
    const dy = at.y - y;
    if (dx * dx + dy * dy <= r2) return true;
  }
  return false;
}

// ─── Детерминированный RNG по seed ────────────────────────────────────────────

/**
 * mulberry32 с 32-битным миксом seed (seed — int64 в [0, 2^53), Number-safe,
 * §3.1): половины seed смешиваются — младшие биты обоих влияют на поток.
 * Одинаковый seed → одинаковая последовательность на любом клиенте.
 */
export function makeRng(seed: number): () => number {
  const lo = Math.floor(seed % 0x100000000);
  const hi = Math.floor(seed / 0x100000000);
  let a = (lo ^ Math.imul(hi, 0x9e3779b1)) >>> 0;
  return function next(): number {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ─── Дрон-спавнер (B2) ────────────────────────────────────────────────────────

/** Спецификация дрона: старт за краем, прямая через точку плитки, скорость. */
export interface DroneSpec {
  /** Момент спавна (мс от старта вопроса). */
  t0Ms: number;
  /** Стартовая точка (за краем плитки). */
  x0: number;
  y0: number;
  /** Единичное направление полёта. */
  dx: number;
  dy: number;
  /** Скорость (плиток/сек). */
  speed: number;
}

/** Отступ старата за краем: покрывает диагональ плитки с запасом (√2). */
const DRONE_ENTER_MARGIN = 1.0;
/** Порог выбывания: дрон улетел далеко за противоположный край. */
const DRONE_EXIT_MARGIN = 2.0;

/**
 * Разворачивает полный список дронов вопроса по seed (B2): первый — не раньше
 * firstDroneMs; далее интервал и скорость линейно растут по прогрессу квиза
 * difficulty01 = index/(total−1) (§3.1: в params только крайние значения,
 * прогрессию считает клиент). Траектория: случайная точка внутри плитки
 * (0.15..0.85 — не по самым углам) + случайный угол; дрон стартует за краем
 * (margin) и летит по прямой до вылета (dronePosAt → null). Джиттер интервала
 * ±25% детерминирован RNG — волны дронов одинаковы у всех.
 *
 * seed < 0/NaN (превью редактора без seed, соло-заглушка) → дронов нет.
 */
export function spawnDrones(
  seed: number,
  params: RunParams,
  difficulty01: number,
  durationMs: number,
): DroneSpec[] {
  if (!Number.isFinite(seed) || seed < 0 || durationMs <= 0) return [];
  const rand = makeRng(seed);
  const d = Math.max(0, Math.min(1, difficulty01));
  const interval =
    params.spawnIntervalStartMs + (params.spawnIntervalEndMs - params.spawnIntervalStartMs) * d;
  const speed = params.droneSpeedStart + (params.droneSpeedEnd - params.droneSpeedStart) * d;

  const out: DroneSpec[] = [];
  let t = params.firstDroneMs;
  while (t <= durationMs) {
    const px = 0.15 + rand() * 0.7;
    const py = 0.15 + rand() * 0.7;
    const ang = rand() * Math.PI * 2;
    const dx = Math.cos(ang);
    const dy = Math.sin(ang);
    out.push({
      t0Ms: Math.round(t),
      x0: px - dx * DRONE_ENTER_MARGIN,
      y0: py - dy * DRONE_ENTER_MARGIN,
      dx,
      dy,
      speed,
    });
    t += Math.max(200, interval * (0.75 + rand() * 0.5));
  }
  return out;
}

/**
 * Позиция дрона в момент elapsedMs (мс от старта вопроса) или null: ещё не
 * заспавнен (t < t0Ms) либо улетел за границу и исчез (план B2).
 */
export function dronePosAt(drone: DroneSpec, elapsedMs: number): { x: number; y: number } | null {
  const dt = (elapsedMs - drone.t0Ms) / 1000;
  if (dt < 0) return null;
  const x = drone.x0 + drone.dx * drone.speed * dt;
  const y = drone.y0 + drone.dy * drone.speed * dt;
  if (x < -DRONE_EXIT_MARGIN || x > 1 + DRONE_EXIT_MARGIN) return null;
  if (y < -DRONE_EXIT_MARGIN || y > 1 + DRONE_EXIT_MARGIN) return null;
  return { x, y };
}

/**
 * Прогрессия сложности «к последнему вопросу» (§3.1): 0 на первом вопросе
 * квиза → 1 на последнем (total ≤ 1 → 0 — один вопрос не «сложный»).
 */
export function runDifficulty(index: number, total: number): number {
  if (total <= 1) return 0;
  return Math.max(0, Math.min(1, index / (total - 1)));
}

// ─── 0.14.3 S1: единая серверная временная база (makeRunClock) ────────────────

/** EMA-коэффициент сглаживания offset по t positions-пакетов (план §2 S1). */
export const RUN_CLOCK_EMA_ALPHA = 0.1;
/** Выброс offset-выборки: |sample − offset| > 500 мс — отбрасываем (джиттер сети/подмена). */
export const RUN_CLOCK_OUTLIER_MS = 500;

/**
 * Часы симуляции run в СЕРВЕРНОМ времени (0.14.3, S1 — лечение D1: у каждого
 * клиента была своя база performance.now() → фазовый сдвиг дронов в сотни мс).
 *
 * offset = serverNow − localNow считается при появлении kindData
 * (kindData.now − Date.now(); Date.now() согласован с Unix-мс сервера), затем
 * уточняется EMA по t каждого positions-пакета (у ведущего; α=0.1, выбросы
 * >500 мс отбрасываются — всплеск задержки не дёргает симуляцию). Симуляция
 * дронов ведётся от startedAtMs: elapsed = serverNow − startedAtMs.
 *
 * Без startedAtMs/now (превью редактора, старый пакет) — offset=0 и база
 * fallbackStartedAt (= question.startedAt): локальные часы, как до 0.14.3.
 */
export interface RunClock {
  /** Текущий offset серверского времени (мс; диагностика/тесты). */
  readonly offset: number;
  /** Серверное «сейчас» для локального now (Unix-мс). */
  serverNow(localNow: number): number;
  /** Мс от старта вопроса (серверная база kindData.startedAtMs, fallback — переданный). */
  elapsedMs(localNow: number, fallbackStartedAt: number): number;
  /** EMA-уточнение offset по t positions-пакета (выбросы отбрасываются). */
  syncPacketT(t: number, localNow: number): void;
}

/** Создаёт часы по kindData run-вопроса (null/без полей — локальные часы). */
export function makeRunClock(
  kindData: { startedAtMs?: number; now?: number } | null | undefined,
  localNow: number,
): RunClock {
  const startedAtMs =
    kindData && Number.isFinite(kindData.startedAtMs) && (kindData.startedAtMs as number) > 0
      ? (kindData.startedAtMs as number)
      : null;
  let offset =
    kindData && Number.isFinite(kindData.now) && (kindData.now as number) > 0
      ? (kindData.now as number) - localNow
      : 0;
  return {
    get offset(): number {
      return offset;
    },
    serverNow(now: number): number {
      return now + offset;
    },
    elapsedMs(now: number, fallbackStartedAt: number): number {
      const base = startedAtMs ?? fallbackStartedAt;
      return now + offset - base;
    },
    syncPacketT(t: number, now: number): void {
      if (!Number.isFinite(t) || t <= 0) return;
      const sample = t - now;
      // Выброс (рассинхрон >500 мс) не усредняем: это разовый сетевой джиттер,
      // а не сдвиг часов — иначе симуляцию дёрнет на полсекунды.
      if (Math.abs(sample - offset) > RUN_CLOCK_OUTLIER_MS) return;
      offset += RUN_CLOCK_EMA_ALPHA * (sample - offset);
    },
  };
}

// ─── 0.14.3 R2: буфер-интерполяция positions у ведущего ──────────────────────

/** Задержка воспроизведения: рендерим на serverNow − 200 мс — между двумя
 *  последними снапшотами всегда есть что интерполировать (~5 Гц-пакеты). */
export const RUN_PLAYBACK_DELAY_MS = 200;
/** Предел экстраполяции за последний снапшот: пакета нет дольше 300 мс —
 *  фигурка замирает на последней позиции (заморозка после close, обрыв потока). */
export const RUN_EXTRAP_MAX_MS = 300;

/** Снапшот позиции игрока из positions-пакета (координаты + серверное t). */
export interface PosSnapshot {
  x: number;
  y: number;
  /** Серверное Unix-мс пакета (PositionsPayload.t). */
  t: number;
}

/** Результат интерполяции: точка + скорость (плиток/мс, для поворота/кадров). */
export interface InterpPos {
  x: number;
  y: number;
  /** Скорость по X (плиток/мс; знак — направление). */
  vx: number;
  /** Скорость по Y (плиток/мс). */
  vy: number;
}

function clampTile(v: number): number {
  if (Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(1, v));
}

/**
 * Позиция игрока на «серверное время renderT» по последним двум снапшотам
 * (R2): между t_prev..t_last — лерп; за t_last — экстраполяция по скорости
 * пары не далее RUN_EXTRAP_MAX_MS (потом — заморозка на последнем снапшоте);
 * до t_prev — координата prev. Скорость — из пары снапшотов (плиток/мс).
 * prev === null (первый пакет игрока) — сразу last, скорость нулевая.
 */
export function interpSnapshots(
  prev: PosSnapshot | null,
  last: PosSnapshot,
  renderT: number,
): InterpPos {
  if (!prev || last.t <= prev.t) {
    return { x: clampTile(last.x), y: clampTile(last.y), vx: 0, vy: 0 };
  }
  const vx = (last.x - prev.x) / (last.t - prev.t);
  const vy = (last.y - prev.y) / (last.t - prev.t);
  if (renderT <= prev.t) return { x: clampTile(prev.x), y: clampTile(prev.y), vx, vy };
  if (renderT >= last.t) {
    const overMs = renderT - last.t;
    // Дольше предела (пакетов нет — игрок встал/вопрос закрыт): позиция
    // заморожена на last + v·MAX и СКОРОСТЬ НУЛЕВАЯ — кадры ходьбы гаснут,
    // фигурка не «бежит на месте» (pos шлётся только при изменении, §3.3).
    if (overMs > RUN_EXTRAP_MAX_MS) {
      return {
        x: clampTile(last.x + vx * RUN_EXTRAP_MAX_MS),
        y: clampTile(last.y + vy * RUN_EXTRAP_MAX_MS),
        vx: 0,
        vy: 0,
      };
    }
    return {
      x: clampTile(last.x + vx * overMs),
      y: clampTile(last.y + vy * overMs),
      vx,
      vy,
    };
  }
  const k = (renderT - prev.t) / (last.t - prev.t);
  return {
    x: clampTile(prev.x + (last.x - prev.x) * k),
    y: clampTile(prev.y + (last.y - prev.y) * k),
    vx,
    vy,
  };
}

// ─── 0.14.3 R3: сглаживание поворота и кадров ходьбы у ведущего ──────────────

/** Гистерезис поворота: дельта курса < 10° не отрабатывается (фигурку не дёргает). */
export const TURN_HYSTERESIS_DEG = 10;
/** Гистерезис скорости idle↔run: ниже 0.02 плиток/с — точно idle, выше 0.05 — точно бег. */
export const WALK_SPEED_IDLE = 0.02;
export const WALK_SPEED_RUN = 0.05;
/** EMA-коэффициент скорости для кадров ходьбы (сглаживает 5 Гц-скачки пакетов). */
export const WALK_SPEED_EMA_ALPHA = 0.2;

/** Кратчайшая дуга между углами (−180..180). */
export function shortestArcDeg(from: number, to: number): number {
  return (((to - from + 540) % 360) + 360) % 360 - 180;
}

/**
 * Поворот к цели по кратчайшей дуге с гистерезисом (R3): |delta| < hysteresis —
 * курс не меняется (микродрожание вектора скорости не вращает фигурку), иначе —
 * лерп-шаг (доля lerp от дельты: плавный доворот за несколько кадров).
 */
export function turnToward(
  heading: number,
  target: number,
  lerp: number,
  hysteresis: number = TURN_HYSTERESIS_DEG,
): number {
  const delta = shortestArcDeg(heading, target);
  if (Math.abs(delta) < hysteresis) return heading;
  return heading + delta * lerp;
}

/**
 * Гистерезис idle↔run для кадров ходьбы (R3): переход idle→run только выше
 * WALK_SPEED_RUN, run→idle только ниже WALK_SPEED_IDLE — между порогами
 * сохраняется прежнее состояние (скорость у порога не мигает кадрами).
 */
export function isWalkingState(running: boolean, speedTilesPerSec: number): boolean {
  if (running) return speedTilesPerSec >= WALK_SPEED_IDLE;
  return speedTilesPerSec > WALK_SPEED_RUN;
}

/**
 * Один шаг EMA-скорости (R3, кадры ходьбы у ведущего): сглаживает мгновенную
 * скорость из интерполяции (α=0.2 — полсекунды установления при 5 Гц-пакетах).
 */
export function emaStep(prev: number, sample: number, alpha: number = WALK_SPEED_EMA_ALPHA): number {
  return prev + (sample - prev) * alpha;
}

// ─── Редизайн 0.14.1: пиксельные эффекты гибели (детерминизм от diedAtMs) ─────
// 0.14.2 (замечание владельца после игротеста): пятно детальнее — три
// «архетипа» формы (клякса/шлейф/кольцо), ядро + капли + дальние брызги-точки
// (слой layer красится своим оттенком красного в SplatSvg).

/** Пиксель пятна/взрыва: смещение от эпицентра в долях плитки + размер + слой. */
export interface PixelCell {
  /** Смещение X от центра (доли плитки; может быть отрицательным). */
  dx: number;
  /** Смещение Y от центра. */
  dy: number;
  /** Сторона пикселя (доли плитки; масштаб кляксы). */
  s: number;
  /** Слой кляксы: 0 — тёмное ядро, 1 — капли, 2 — дальние брызги-точки. */
  layer: 0 | 1 | 2;
}

/** Архетип кляксы: 0 — расплывшаяся клякса, 1 — направленный шлейф, 2 — кольцо. */
export type SplatArchetype = 0 | 1 | 2;

/** Число архетипов пятна (детерминированный выбор от diedAtMs). */
export const SPLAT_ARCHETYPES = 3;

/**
 * Архетип пятна по diedAtMs (первый rand()-забор потока спавнера кляксы) —
 * наружу для тестов и одинаковой развёртки у игрока и ведущего.
 */
export function splatArchetype(diedAtMs: number): SplatArchetype {
  const rand = makeRng(diedAtMs >>> 0);
  return Math.floor(rand() * SPLAT_ARCHETYPES) as SplatArchetype;
}

/**
 * Форма красного пятна гибели — пиксельная клякса, ДЕТЕРМИНИРОВАННАЯ от
 * diedAtMs (makeRng — единственный RNG-источник, новых не вводим: одинаковая
 * клякса у игрока и ведущего по одному моменту гибели).
 *
 * 0.14.2: три архетипа (~18–28 клеток, детальнее прежних 8–12):
 *  0 «клякса» — плотное ядро 2×2 с тёмными вкраплениями + 8–12 капель по
 *    компасу + 3–5 дальних брызг;
 *  1 «шлейф» — компактное ядро + направленный выброс капель вдоль случайного
 *    угла (с сужением — «брызги летели по ходу») + контр-капля + 2–4 брызги;
 *  2 «кольцо» — ядро + 8–10 капель по кругу одного радиуса + 2–3 брызги.
 * Слои: layer 0 (ядро) — тёмный, 1 (капли) — основной, 2 (брызги) — светлый.
 */
export function splatCells(diedAtMs: number): PixelCell[] {
  const rand = makeRng(diedAtMs >>> 0);
  const archetype = Math.floor(rand() * SPLAT_ARCHETYPES) as SplatArchetype;
  const cells: PixelCell[] = [];
  // Ядро кляксы — плотный блок 2×2 в центре (размер от архетипа: шлейф —
  // компактнее, клякса/кольцо — плотнее).
  const core = archetype === 1 ? 0.022 + rand() * 0.005 : 0.026 + rand() * 0.008;
  for (const dx of [-core, 0]) {
    for (const dy of [-core, 0]) cells.push({ dx, dy, s: core * 2, layer: 0 });
  }
  // Тёмные вкрапления ядра (детализация 0.14.2): 1–2 пикселя поверх блока.
  const mottles = 1 + Math.floor(rand() * 2);
  for (let i = 0; i < mottles; i++) {
    cells.push({
      dx: (rand() - 0.5) * core,
      dy: (rand() - 0.5) * core,
      s: core * (0.7 + rand() * 0.5),
      layer: 0,
    });
  }
  // Капли по компасу (направления N/E/S/W + диагонали).
  const dirs = [
    [0, -1],
    [1, 0],
    [0, 1],
    [-1, 0],
    [-0.7, -0.7],
    [0.7, 0.7],
    [-0.7, 0.7],
    [0.7, -0.7],
  ];
  const drop = (dx: number, dy: number, dist: number): void => {
    cells.push({
      dx: dx * dist,
      dy: dy * dist,
      s: 0.011 + rand() * 0.013,
      layer: 1,
    });
  };
  const fleck = (dx: number, dy: number, dist: number): void => {
    cells.push({
      dx: dx * dist,
      dy: dy * dist,
      s: 0.006 + rand() * 0.006,
      layer: 2,
    });
  };
  if (archetype === 1) {
    // Шлейф: направленный выброс вдоль угла ang — капли вдоль вектора с
    // джиттером поперёк, дальние — мельче (сужающийся след).
    const ang = rand() * Math.PI * 2;
    const ux = Math.cos(ang);
    const uy = Math.sin(ang);
    const px = -uy; // перпендикуляр.
    const py = ux;
    const n = 7 + Math.floor(rand() * 3); // 7..9 капель следа.
    for (let i = 0; i < n; i++) {
      const t = (i + 1) / n; // 0..1 по длине следа.
      const dist = 0.02 + t * (0.03 + rand() * 0.015);
      const side = (rand() - 0.5) * 0.016 * (1 - t * 0.5);
      drop(ux + px * side * 8, uy + py * side * 8, dist);
    }
    drop(-ux, -uy, 0.018 + rand() * 0.012); // контр-капля («отдача»).
    const sparks = 2 + Math.floor(rand() * 3); // 2..4 брызг по направлению.
    for (let i = 0; i < sparks; i++) {
      fleck(ux + (rand() - 0.5) * 0.4, uy + (rand() - 0.5) * 0.4, 0.05 + rand() * 0.02);
    }
    return cells;
  }
  if (archetype === 2) {
    // Кольцо: капли по кругу одного радиуса (контур «разбрызгалось в стороны»).
    const n = 8 + Math.floor(rand() * 3); // 8..10 капель по кругу.
    const radius = 0.026 + rand() * 0.012;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + rand() * 0.25;
      drop(Math.cos(a), Math.sin(a), radius + rand() * 0.006);
    }
    const sparks = 2 + Math.floor(rand() * 2);
    for (let i = 0; i < sparks; i++) {
      const a = rand() * Math.PI * 2;
      fleck(Math.cos(a), Math.sin(a), 0.05 + rand() * 0.02);
    }
    return cells;
  }
  // Архетип 0 «клякса»: расплывшаяся — капли по компасу плотнее и дальше.
  const extra = 8 + Math.floor(rand() * 5); // 8..12 капель.
  for (let i = 0; i < extra; i++) {
    const d = dirs[Math.floor(rand() * dirs.length)];
    drop(d[0], d[1], 0.02 + rand() * 0.032);
  }
  const sparks = 3 + Math.floor(rand() * 3); // 3..5 брызг.
  for (let i = 0; i < sparks; i++) {
    const d = dirs[Math.floor(rand() * dirs.length)];
    fleck(d[0] + (rand() - 0.5) * 0.3, d[1] + (rand() - 0.5) * 0.3, 0.048 + rand() * 0.022);
  }
  return cells;
}

/** Осколок взрыва: направление/дальность/размер — детерминировано от diedAtMs. */
export interface ShardSpec {
  /** Единичный вектор разлёта. */
  dx: number;
  /** Единичный вектор разлёта (Y). */
  dy: number;
  /** Дальность разлёта (доли плитки, 0.05–0.13). */
  dist: number;
  /** Пиксель осколка (доли плитки). */
  s: number;
  /** Задержка старта (сек, 0–0.08 — лёгкая неодновременность). */
  delay: number;
}

/**
 * Разлёт пикселей-осколков взрыва (10–16 частиц, вид сверху — гравитации нет:
 * скорость + затухание имитируются CSS-easing к конечной точке). Форма
 * детерминирована diedAtMs (тот же RNG-источник, что у пятна) — взрыв
 * воспроизводим у игрока и ведущего.
 */
export function explosionShards(diedAtMs: number): ShardSpec[] {
  const rand = makeRng((diedAtMs ^ 0x5f3759df) >>> 0);
  const n = 10 + Math.floor(rand() * 7); // 10..16.
  const shards: ShardSpec[] = [];
  for (let i = 0; i < n; i++) {
    const ang = (i / n) * Math.PI * 2 + rand() * 0.5; // круг + джиттер.
    const dist = 0.05 + rand() * 0.08;
    shards.push({
      dx: Math.cos(ang),
      dy: Math.sin(ang),
      dist,
      s: 0.008 + rand() * 0.008,
      delay: rand() * 0.08,
    });
  }
  return shards;
}

/** Палитра различимости человечков ведущего (HSL: насыщенные, светлые). */
const PLAYER_HUES = [
  4, 28, 46, 68, 96, 130, 160, 186, 205, 230, 258, 286, 310, 335, 18, 55,
];

/**
 * Цвет человечка per-player — стабильный хэш id (FNV-1a) → индекс палитры
 * HSL: десятки фигурок у ведущего различимы по цвету без коллизий соседних
 * id (палитра из 16 удалённых оттенков; детерминизм — одинаковый цвет у
 * игрока и на канве ведущего).
 */
export function playerColor(id: number, seed = 0): string {
  let h = 2166136261 ^ (seed | 0);
  h = Math.imul(h ^ id, 16777619) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 16777619) >>> 0;
  const hue = PLAYER_HUES[h % PLAYER_HUES.length];
  return `hsl(${hue} 85% 62%)`;
}
